import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Column, Columns, Skeleton } from "@aviary";
import {
  BrandName,
  ProductAddToCartWrapper,
  ProductImageWithTag,
  ProductInformation,
  ProductName,
  ProductUnitPricing,
  SmartProductImage,
} from "@shared/components/ProductCard";
import { ProductAvailabilityBanner } from "@shared/components/ProductCard/ProductAvailabilityBanner";
import { useVariantImagesQuery, useVariantNewImagesQuery } from "@shared/data/queries";
import { useBreakpoints, useFlippers } from "@shared/hooks";
import { l } from "@shared/locales/i18n";
import type { AvailabilityStatus } from "@shared/types/availabilityStatus";
import type { Variant, Product } from "@shared/types/graphqlGenerated";
import { Size } from "@shared/types/sizes";
import { canUseWebp } from "@shared/utils";

import { OnlyOnFullscriptTag } from "../ProductCard/OnlyOnFullscriptTag";
import { ProductVariantOptions } from "../ProductVariantOptions";

import * as styles from "./ProductDetailsCard.styles";

interface VariantType
  extends Pick<
    Variant,
    | "id"
    | "descriptor"
    | "expectedReturnDate"
    | "image"
    | "imageNew"
    | "msrp"
    | "sku"
    | "variantType"
  > {
  dailyPack?: {
    id: string;
    nameWithSupply: string;
  };
  availabilityStatus?: Variant["availabilityStatus"];
}

interface Props {
  brandName: string;
  brandLink?: string;
  currencySymbol?: "CAN" | "US";
  currentVariantId: string;
  discountPrice?: number;
  isPriceLoading?: boolean;
  isWholesale?: boolean;
  onVariantSelect?: (variantId: string) => void;
  price?: number;
  product: Pick<Product, "id" | "description" | "name">;
  promotionPrice?: number;
  unitPostfix?: string;
  variants: VariantType[];
  children: ReactNode;
  blurPrice?: boolean;
}

const ProductDetailsCard = ({
  brandName,
  brandLink,
  children,
  currencySymbol,
  currentVariantId,
  discountPrice,
  isPriceLoading,
  isWholesale,
  onVariantSelect,
  price,
  product,
  promotionPrice,
  unitPostfix,
  variants,
  blurPrice,
}: Props) => {
  const { tablet } = useBreakpoints();
  const { t } = useTranslation();
  const [threeSixtyImagesFlipper, dailyPackPhase2Flipper] = useFlippers(
    "threesixty_images_ian",
    "cura_daily_packs_phase_2"
  );
  const currentVariant = variants.find(variant => variant.id === currentVariantId);
  const isDailyPack = currentVariant?.variantType !== "coyote";
  const isDynamicDailyPack = currentVariant?.variantType === "dynamic_daily_pack";
  const { id: variantId, image, imageNew, availabilityStatus, expectedReturnDate } = currentVariant;

  const { data: imagesData } = useVariantImagesQuery({
    variables: { variantId },
    skip: threeSixtyImagesFlipper || tablet.lessThan,
  });

  const { data: imagesNewData } = useVariantNewImagesQuery({
    variables: {
      variantId,
      size: tablet.lessThan ? "MEDIUM" : "LARGE",
      format: canUseWebp() ? "WEBP" : "PNG",
    },
    skip: !threeSixtyImagesFlipper,
  });

  const imageSize = () => (tablet.lessThan ? Size.LARGE : Size.XLARGE);

  const newImages = imagesNewData?.viewer?.variant?.imagesNew;

  const getNewImage = () => {
    return newImages || [imageNew];
  };

  const renderProductImage = () => {
    const largeImage = imagesData?.viewer?.variant?.images[0]?.urlLarge;

    const getImage = () => largeImage || image;

    return threeSixtyImagesFlipper ? (
      <SmartProductImage
        expectedReturnDate={expectedReturnDate}
        variantId={variantId}
        isSize={imageSize()}
        images={getNewImage()}
        name={product.name}
        availabilityStatus={availabilityStatus as AvailabilityStatus}
      />
    ) : (
      <ProductImageWithTag
        expectedReturnDate={expectedReturnDate}
        availabilityStatus={availabilityStatus}
        isSize={imageSize()}
        image={getImage()}
        name={product.name}
      />
    );
  };

  const renderVariantOptions = () => {
    if (dailyPackPhase2Flipper && isDynamicDailyPack) {
      return null;
    }

    return (
      <ProductVariantOptions
        css={styles.variantOptions}
        currentVariantId={currentVariantId}
        variants={variants.map(variant => ({
          id: variant.id,
          availabilityStatus: variant.availabilityStatus as AvailabilityStatus,
          descriptor: variant.descriptor,
          expectedReturnDate: variant.expectedReturnDate,
        }))}
        onSelect={onVariantSelect}
      />
    );
  };

  const renderProductAvailabilityBanner = () => (
    <ProductAvailabilityBanner
      availabilityStatus={availabilityStatus as AvailabilityStatus}
      expectedReturnDate={expectedReturnDate}
    />
  );

  const renderProductUnitPricing = () => {
    if (isPriceLoading) {
      return <Skeleton type="typography" typographySize="h5" margin="topAndBottom" />;
    }

    const hidePrice = blurPrice || (!price && !discountPrice);

    return (
      <ProductUnitPricing
        msrp={price}
        sellPrice={discountPrice}
        promotionPrice={promotionPrice}
        unitPostfix={unitPostfix}
        isWholesale={isWholesale}
        currencySymbol={currencySymbol}
        blur={hidePrice}
      />
    );
  };

  const getName = () => {
    if (dailyPackPhase2Flipper && isDynamicDailyPack) {
      return currentVariant?.dailyPack?.nameWithSupply;
    }

    return product.name;
  };

  const getBrand = () => {
    if (dailyPackPhase2Flipper && isDynamicDailyPack) {
      return t(l.sharedCatalog.personalizedByYou);
    }

    return brandName;
  };

  return (
    <Columns css={styles.columns}>
      <Column css={styles.detailsColumn} columnWidth={7}>
        <ProductInformation css={styles.productInformation}>
          <div css={styles.nameSection}>
            <ProductName type="h2" css={styles.productName} name={getName()} />
            {isDailyPack && <OnlyOnFullscriptTag />}
          </div>
          <BrandName name={getBrand()} to={!isDynamicDailyPack && brandLink} />
          {renderVariantOptions()}
          {renderProductAvailabilityBanner()}
          {renderProductUnitPricing()}
        </ProductInformation>
        <ProductAddToCartWrapper topMarginSize={Size.LARGE}>{children}</ProductAddToCartWrapper>
      </Column>
      <Column css={styles.imageColumn} columnWidth={5}>
        {renderProductImage()}
      </Column>
    </Columns>
  );
};

export { ProductDetailsCard, type VariantType };
